import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router';
import { User, Site } from '../../../types';
import FormFieldSet from './FormFieldSet';
import FloatingLabel from './FloatingLabel';
import { getCookie } from '../../../utils';
import { getProject } from '../../energy/api';
import { PlusIcon, XMarkIcon, ExclamationTriangleIcon, CheckIcon } from '@heroicons/react/20/solid';

interface InstallerAssignment {
    id: string;
    selectedSiteIds: number[];
    email: string;
    removeInstaller: boolean;
}

const InstallerInvitationPage: React.FC = () => {
    const { user } = useOutletContext<{ user: User }>();
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [sites, setSites] = useState<Site[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [installerAssignments, setInstallerAssignments] = useState<InstallerAssignment[]>([
        { id: crypto.randomUUID(), selectedSiteIds: [], email: '', removeInstaller: false }
    ]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchProject = async () => {
            try {
                const project = await getProject(projectId!);
                setSites(project.sites || []);
            } catch (error) {
                console.error('Failed to fetch project:', error);
                alert('Failed to load project sites. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchProject();
    }, [projectId]);

    const { sitesWithInstaller, sitesWithoutInstaller } = useMemo(() => {
        return {
            sitesWithInstaller: sites.filter(site => site.installer !== null),
            sitesWithoutInstaller: sites.filter(site => site.installer === null)
        };
    }, [sites]);

    const allSelectedSiteIds = installerAssignments.flatMap(assignment => assignment.selectedSiteIds);

    const toggleSiteSelection = (assignmentId: string, siteId: number) => {
        setInstallerAssignments(prev => prev.map(assignment => {
            if (assignment.id === assignmentId) {
                const isSelected = assignment.selectedSiteIds.includes(siteId);
                return {
                    ...assignment,
                    selectedSiteIds: isSelected 
                        ? assignment.selectedSiteIds.filter(id => id !== siteId)
                        : [...assignment.selectedSiteIds, siteId]
                };
            }
            return assignment;
        }));
    };

    const selectAllSites = (assignmentId: string, siteIds: number[]) => {
        setInstallerAssignments(prev => prev.map(assignment => {
            if (assignment.id === assignmentId) {
                const availableSiteIds = siteIds.filter(id => !allSelectedSiteIds.includes(id) || assignment.selectedSiteIds.includes(id));
                const newSelectedIds = [...new Set([...assignment.selectedSiteIds, ...availableSiteIds])];
                return {
                    ...assignment,
                    selectedSiteIds: newSelectedIds
                };
            }
            return assignment;
        }));
    };

    const unselectAllSites = (assignmentId: string, siteIds: number[]) => {
        setInstallerAssignments(prev => prev.map(assignment => {
            if (assignment.id === assignmentId) {
                return {
                    ...assignment,
                    selectedSiteIds: assignment.selectedSiteIds.filter(id => !siteIds.includes(id))
                };
            }
            return assignment;
        }));
    };

    const updateEmail = (assignmentId: string, email: string) => {
        setInstallerAssignments(prev => prev.map(assignment => 
            assignment.id === assignmentId ? { ...assignment, email } : assignment
        ));
    };

    const toggleRemoveInstaller = (assignmentId: string) => {
        setInstallerAssignments(prev => prev.map(assignment => 
            assignment.id === assignmentId 
                ? { ...assignment, removeInstaller: !assignment.removeInstaller } 
                : assignment
        ));
    };

    const allSelectedSitesHaveInstallers = (assignment: InstallerAssignment) => {
        if (assignment.selectedSiteIds.length === 0) return false;
        return assignment.selectedSiteIds.every(siteId => 
            sitesWithInstaller.some(site => site.id === siteId)
        );
    };

    const addInstallerSection = () => {
        setInstallerAssignments(prev => [
            ...prev,
            { id: crypto.randomUUID(), selectedSiteIds: [], email: '', removeInstaller: false }
        ]);
    };

    const removeInstallerSection = (assignmentId: string) => {
        setInstallerAssignments(prev => prev.filter(assignment => assignment.id !== assignmentId));
    };

    const isFormComplete = installerAssignments.every(assignment => 
        assignment.selectedSiteIds.length > 0 && 
        (assignment.removeInstaller || assignment.email.trim() !== '')
    );

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const assignments = installerAssignments.map(assignment => ({
                site_ids: assignment.selectedSiteIds,
                installer_email: assignment.removeInstaller ? '' : assignment.email
            }));

            const response = await fetch(`/api/installers/bulk-assign/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken') || '',
                },
                body: JSON.stringify({ assignments }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to assign installers');
            }

            const result = await response.json();
            
            if (result.errors && result.errors.length > 0) {
                const errorMessages = result.errors.map((err: any) => 
                    `${err.installer_email || 'Unknown'}: ${err.error}`
                ).join('\n');
                alert(`Some assignments failed:\n${errorMessages}`);
            }

            if (result.success || result.results.length > 0) {
                navigate(`/dashboard/project/${projectId}`);
                navigate(0);
            }
        } catch (error) {
            console.error(error);
            alert('Failed to assign installer(s). Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="p-6 flex justify-center items-center h-full">
                <span className="loading loading-spinner loading-lg"></span>
            </div>
        );
    }

    return (
        <div className="p-6 flex flex-col gap-6 justify-start items-center h-full overflow-y-auto">
            <div className="text-center">
                <h1 className="text-xl font-bold">Installer Registration</h1>
                <p>Register installers for sites in this project</p>
            </div>

            <form onSubmit={handleSubmit} className="w-full max-w-3xl">
                <FormFieldSet className="flex flex-col gap-6">
                    {installerAssignments.map((assignment, index) => (
                        <div key={assignment.id} className="flex flex-col gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                            {installerAssignments.length > 1 && (
                                <div className="flex justify-between items-center">
                                    <h3 className="font-semibold text-base">Installer {index + 1}</h3>
                                    <button
                                        type="button"
                                        onClick={() => removeInstallerSection(assignment.id)}
                                        className="btn btn-ghost btn-sm"
                                        disabled={isSubmitting}
                                    >
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            )}

                            <div>
                                <span className="font-semibold text-sm">Select Sites</span>
                                <div className="flex flex-col gap-4 mt-2 p-3 bg-white rounded border border-gray-300 min-h-[60px]">
                                    {sites.length === 0 ? (
                                        <p className="text-xs text-gray-500">No sites available in this project</p>
                                    ) : (
                                        <>
                                            {sitesWithoutInstaller.length > 0 && (
                                                 <div className="flex flex-col gap-2">
                                                     <div className="flex items-center justify-between">
                                                         <h4 className="text-xs font-semibold text-gray-700">No Installer</h4>
                                                        <div className="flex gap-2">
                                                            Select
                                                            <button
                                                                type="button"
                                                                onClick={() => selectAllSites(assignment.id, sitesWithoutInstaller.map(s => s.id))}
                                                                className="text-xs text-neutral hover:underline hover:cursor-pointer"
                                                                disabled={isSubmitting}
                                                            >
                                                                All
                                                            </button>
                                                            <span className="text-xs text-gray-400">|</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => unselectAllSites(assignment.id, sitesWithoutInstaller.map(s => s.id))}
                                                                className="text-xs text-neutral hover:underline hover:cursor-pointer"
                                                                disabled={isSubmitting}
                                                            >
                                                                None
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {sitesWithoutInstaller.map(site => {
                                                            const isSelected = assignment.selectedSiteIds.includes(site.id);
                                                            const isDisabledDueToOtherAssignment = allSelectedSiteIds.includes(site.id) && !isSelected;
                                                            
                                                            return (
                                                                <button
                                                                    key={site.id}
                                                                    type="button"
                                                                    onClick={() => !isDisabledDueToOtherAssignment && toggleSiteSelection(assignment.id, site.id)}
                                                                    disabled={isDisabledDueToOtherAssignment || isSubmitting}
                                                                    className={`
                                                                        badge badge-sm gap-1 py-2 px-2 transition-all
                                                                        ${isSelected 
                                                                            ? 'badge-neutral' 
                                                                            : isDisabledDueToOtherAssignment
                                                                                ? 'badge-ghost opacity-40 cursor-not-allowed'
                                                                                : 'badge-outline badge-neutral cursor-pointer'
                                                                        }
                                                                    `}
                                                                >
                                                                    {isSelected ? <CheckIcon className="w-3 h-3" /> : <PlusIcon className="w-3 h-3" />}
                                                                    <span>{site.name}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {sitesWithInstaller.length > 0 && (
                                                 <div className="flex flex-col gap-2">
                                                     <div className="flex items-center justify-between">
                                                         <h4 className="text-xs font-semibold text-gray-700">With Installer Registered</h4>
                                                        <div className="flex gap-2">
                                                            Select
                                                            <button
                                                                type="button"
                                                                onClick={() => selectAllSites(assignment.id, sitesWithInstaller.map(s => s.id))}
                                                                className="text-xs text-warning hover:underline hover:cursor-pointer"
                                                                disabled={isSubmitting}
                                                            >
                                                                All
                                                            </button>
                                                            <span className="text-xs text-gray-400">|</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => unselectAllSites(assignment.id, sitesWithInstaller.map(s => s.id))}
                                                                className="text-xs text-warning hover:underline hover:cursor-pointer"
                                                                disabled={isSubmitting}
                                                            >
                                                                None
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {sitesWithInstaller.map(site => {
                                                            const isSelected = assignment.selectedSiteIds.includes(site.id);
                                                            const isDisabledDueToOtherAssignment = allSelectedSiteIds.includes(site.id) && !isSelected;
                                                            
                                                            return (
                                                                <button
                                                                    key={site.id}
                                                                    type="button"
                                                                    onClick={() => !isDisabledDueToOtherAssignment && toggleSiteSelection(assignment.id, site.id)}
                                                                    disabled={isDisabledDueToOtherAssignment || isSubmitting}
                                                                    className={`
                                                                        badge badge-sm gap-1 py-2 px-2 transition-all
                                                                        ${isSelected 
                                                                            ? 'badge-warning' 
                                                                            : isDisabledDueToOtherAssignment
                                                                                ? 'badge-ghost opacity-40 cursor-not-allowed'
                                                                                : 'badge-outline badge-warning cursor-pointer'
                                                                        }
                                                                    `}
                                                                >
                                                                    {isSelected ? <CheckIcon className="w-3 h-3" /> : <PlusIcon className="w-3 h-3" />}
                                                                    <span>{site.name} ({site.installer?.name || site.installer?.email})</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                    {assignment.selectedSiteIds.some(siteId => 
                                                        sitesWithInstaller.some(site => site.id === siteId)
                                                    ) && (
                                                         <div className="alert alert-warning py-2 px-3 flex items-start gap-2">
                                                             <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                                             <span className="text-xs">
                                                                 Selecting sites with registered installers will overwrite the existing installer assignment.
                                                             </span>
                                                         </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                {assignment.selectedSiteIds.length > 0 && (
                                    <p className="text-xs text-gray-600 mt-1">
                                        {assignment.selectedSiteIds.length} site(s) selected
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-col gap-3">
                                {allSelectedSitesHaveInstallers(assignment) && (
                                    <div className="form-control">
                                        <label className="label cursor-pointer justify-start gap-3 p-0">
                                            <input
                                                type="checkbox"
                                                checked={assignment.removeInstaller}
                                                onChange={() => toggleRemoveInstaller(assignment.id)}
                                                className="checkbox checkbox-error"
                                                disabled={isSubmitting}
                                            />
                                            <span className="label-text text-sm font-semibold text-error">
                                                Remove installer from selected sites
                                            </span>
                                        </label>
                                    </div>
                                )}
                                <FloatingLabel>
                                    <span className="font-semibold text-sm">Installer E-mail</span>
                                    <input
                                        type="email"
                                        value={assignment.email}
                                        onChange={(e) => updateEmail(assignment.id, e.target.value)}
                                        className="input focus:outline-none w-full"
                                        placeholder="installer@example.com"
                                        required={!assignment.removeInstaller}
                                        disabled={isSubmitting || assignment.removeInstaller}
                                    />
                                </FloatingLabel>
                            </div>
                    </div>
                    ))}

                    <button
                        type="button"
                        onClick={addInstallerSection}
                        className="btn btn-outline btn-neutral w-full"
                        disabled={isSubmitting}
                    >
                        <PlusIcon className="w-5 h-5" />
                        Add Another Installer
                    </button>

                    <div className="flex gap-4 justify-between mt-4">
                        <button 
                            type="button" 
                            className="btn btn-ghost flex-1" 
                            onClick={() => navigate(`/dashboard/project/${projectId}`)} 
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="btn btn-neutral flex-1" 
                            disabled={isSubmitting || !isFormComplete}
                        >
                            {isSubmitting && <span className="loading loading-spinner"></span>}
                            {isSubmitting ? 'Assigning...' : 'Assign Installers'}
                        </button>
                    </div>
                </FormFieldSet>
            </form>
        </div>
    );
};

export default InstallerInvitationPage;